import React from 'react'
import type { LucideIcon } from 'lucide-react'

export type ViewToggleOption<T extends string> = {
  value: T
  label: string
  icon: LucideIcon
}

export interface ViewToggleProps<T extends string> {
  value: T
  options: ViewToggleOption<T>[]
  onChange: (value: T) => void
  'aria-label'?: string
  className?: string
  'data-testid'?: string
  /** When true, show text labels beside icons (default: icon-only). */
  showLabels?: boolean
}

export default function ViewToggle<T extends string>({
  value,
  options,
  onChange,
  'aria-label': ariaLabel = 'View mode',
  className = '',
  'data-testid': dataTestId,
  showLabels = false
}: ViewToggleProps<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-testid={dataTestId}
      className={`inline-flex rounded-lg border border-[#E2E5EA] bg-page-bg p-[3px] ${className}`}
    >
      {options.map((option) => {
        const Icon = option.icon
        const active = value === option.value
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            aria-label={option.label}
            title={option.label}
            onClick={() => onChange(option.value)}
            onMouseDown={(e) => e.preventDefault()}
            className={`inline-flex items-center justify-center rounded-md transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blueprint-blue/40 ${
              showLabels
                ? 'gap-1.5 px-3 py-1 text-[11px]'
                : 'h-8 w-8'
            } ${
              active
                ? 'bg-white font-semibold text-blueprint-blue shadow-[0_1px_3px_rgba(0,0,0,0.06)] active:bg-white'
                : 'bg-transparent font-medium text-heritage-grey hover:text-blueprint-blue active:bg-transparent'
            }`}
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden />
            {showLabels && <span>{option.label}</span>}
          </button>
        )
      })}
    </div>
  )
}
